import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import { Logo } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { http } from '@/lib/api/client'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { Resource } from '@/lib/api/types'

const readToken = () => new URLSearchParams(window.location.hash.replace(/^#/, '')).get('token') ?? ''

/** From an owner's invitation email: set a name and password, then sign in (with two-step setup). */
export function AcceptInvitePage() {
  const [token] = useState(readToken)
  const invitation = useQuery({
    queryKey: ['staff-invitation', token],
    queryFn: async () =>
      (
        await http.post<Resource<{ email: string; name: string; role: string; valid: boolean }>>(
          '/auth/invitation',
          { token },
        )
      ).data,
    enabled: token !== '',
    retry: false,
  })
  const accept = useMutation({
    mutationFn: (input: { name: string; password: string; password_confirmation: string }) =>
      http.post('/auth/invitation/accept', { token, ...input }),
  })
  const [name, setName] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const fieldErrors = isApiError(accept.error) ? accept.error.errors : {}

  return (
    <main className="grid min-h-dvh place-items-center bg-background px-4 py-10">
      <div className="grid w-full max-w-sm gap-6">
        <div className="grid justify-items-center gap-1 text-center">
          <Logo tone="dark" className="mb-3" />
          <p className="eyebrow">Arva SEO staff only</p>
          <h1 className="display text-3xl text-ink">Join the team</h1>
        </div>
        <section className="grid gap-5 rounded-lg border border-border bg-surface p-6 shadow-card">
          {token === '' || invitation.isError || invitation.data?.valid === false ? (
            <div className="grid gap-2 text-[13px] text-ink-muted">
              <p className="font-semibold text-ink">This invitation doesn’t work</p>
              <p>It may have expired or been cancelled. Ask an owner to send a new one.</p>
            </div>
          ) : invitation.isPending ? (
            <Skeleton className="h-48" />
          ) : accept.isSuccess ? (
            <div className="grid gap-3 text-[13px] text-ink-muted">
              <p className="font-semibold text-ink">Your password is set</p>
              <p>Sign in next. You’ll add Arva SEO to an authenticator app and save recovery codes.</p>
              <Button asChild>
                <Link to="/sign-in">Sign in</Link>
              </Button>
            </div>
          ) : (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                accept.mutate({
                  name: (name ?? invitation.data.name).trim(),
                  password,
                  password_confirmation: confirm,
                })
              }}
            >
              <p className="text-[13px] text-ink-muted">
                For <span className="font-medium text-ink">{invitation.data.email}</span>.
              </p>
              <Field label="Your name" htmlFor="name" required error={fieldErrors.name?.[0]}>
                <Input
                  id="name"
                  value={name ?? invitation.data.name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </Field>
              <Field
                label="Password"
                htmlFor="password"
                required
                error={fieldErrors.password?.[0]}
                hint="At least 12 characters."
              >
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              <Field label="Password again" htmlFor="password-confirm" required>
                <Input
                  id="password-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              {accept.isError && !isApiError(accept.error) && (
                <p className="text-xs text-danger">{errorMessage(accept.error)}</p>
              )}
              {isApiError(accept.error) && !accept.error.isValidation && (
                <p className="text-xs text-danger">{accept.error.message}</p>
              )}
              <Button
                type="submit"
                loading={accept.isPending}
                disabled={password.length < 12 || password !== confirm}
              >
                Set password
              </Button>
            </form>
          )}
        </section>
      </div>
    </main>
  )
}
